// Detailseite für einen einzelnen Mandanten.
//
// Funktionen:
//  - Stammdaten (inkl. Adresse, Kundennr./Mitgliedsnr., Vertrag + Laufzeit)
//    inline editieren und speichern
//  - Notizen als WYSIWYG-Markdown-Editor (Toast UI)
//  - Verknüpfte Ereignisse anzeigen + verwalten (Status inline)
//  - Rechnungen: anlegen, bearbeiten, Status wechseln, löschen
//  - Neues Ereignis / Rechnung direkt für diesen Mandanten anlegen
//    (mandant_id ist vorausgewählt)
//  - Den Mandanten selbst löschen → Redirect zurück zur Liste

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api.js';
import { courseSetLabel } from '../courseSets.js';
import EventFormModal from './EventFormModal.jsx';
import InvoiceFormModal from './InvoiceFormModal.jsx';
import MarkdownEditor from './MarkdownEditor.jsx';
import StatusBadge from './StatusBadge.jsx';

const LICENSE_OPTIONS = ['Basis', 'Pro', 'Business', 'Enterprise'];
const EVENT_STATUSES = ['offen', 'in Arbeit', 'erledigt'];
const INVOICE_STATUSES = ['offen', 'bezahlt', 'storniert'];

const EMPTY_FORM = {
    customer_no: '',
    member_no: '',
    name: '',
    contact: '',
    email: '',
    street: '',
    zip: '',
    city: '',
    contract: '',
    contract_start: '',
    license: 'Basis',
    course_set: 'Standard',
    notes: '',
    course_assignment: '',
    active: 1,
};

export default function MandantDetail() {
    const { id: idParam } = useParams();
    const navigate = useNavigate();
    const id = Number(idParam);

    const [data, setData] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const [savedNotice, setSavedNotice] = useState(false);

    const [mandantenList, setMandantenList] = useState([]); // für Modal-Dropdowns
    const [courseSets, setCourseSets] = useState([]);
    const [creatingEvent, setCreatingEvent] = useState(false);
    const [creatingInvoice, setCreatingInvoice] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoadError('');
            const d = await api.getMandant(id);
            setData(d);
            setForm({
                customer_no: d.customer_no ?? '',
                member_no: d.member_no ?? '',
                name: d.name ?? '',
                contact: d.contact ?? '',
                email: d.email ?? '',
                street: d.street ?? '',
                zip: d.zip ?? '',
                city: d.city ?? '',
                contract: d.contract ?? '',
                contract_start: d.contract_start ?? '',
                license: d.license ?? 'Basis',
                course_set: d.course_set ?? 'Standard',
                notes: d.notes ?? '',
                course_assignment: d.course_assignment ?? '',
                active: d.active === undefined ? 1 : Number(d.active),
            });
        } catch (err) {
            setLoadError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
        api.listMandanten().then(setMandantenList).catch(() => { /* unkritisch */ });
        // Kurssets kommen aus dem Backend (einzige Quelle der Wahrheit).
        api.listTotaraCourseSets().then(setCourseSets).catch(() => { /* unkritisch */ });
    }, [load]);

    function updateField(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
        setSavedNotice(false);
    }

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setSaveError('');
        setSavedNotice(false);
        try {
            await api.updateMandant(id, form);
            await load();
            setSavedNotice(true);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    }

    // Sofortiger Toggle aus der Toolbar: schreibt aktiv-Status direkt
    // gegen die API, ohne dass der User auf "Speichern" klicken muss.
    // Damit ist die Aktion schnell zugänglich und der Status spiegelt
    // sich sofort in der Liste/Banner.
    async function handleToggleActive() {
        const next = Number(form.active) === 1 ? 0 : 1;
        try {
            await api.updateMandant(id, { active: next });
            setForm((f) => ({ ...f, active: next }));
            setData((prev) => (prev ? { ...prev, active: next } : prev));
        } catch (err) {
            alert('Status-Update fehlgeschlagen: ' + err.message);
        }
    }

    async function handleDeleteMandant() {
        if (!window.confirm(`Mandant "${data.name}" wirklich löschen? Verknüpfte Ereignisse verlieren die Zuordnung, Rechnungen werden mitgelöscht.`)) {
            return;
        }
        try {
            await api.deleteMandant(id);
            navigate('/mandanten');
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    async function handleDeleteEvent(eventId) {
        if (!window.confirm('Dieses Ereignis wirklich löschen?')) return;
        try {
            await api.deleteEvent(eventId);
            await load();
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    async function handleEventStatusChange(eventId, status) {
        try {
            await api.updateEventStatus(eventId, status);
            setData((prev) => ({
                ...prev,
                events: prev.events.map((e) => (e.id === eventId ? { ...e, status } : e)),
            }));
        } catch (err) {
            alert('Status-Update fehlgeschlagen: ' + err.message);
        }
    }

    async function handleInvoiceStatusChange(invoiceId, status) {
        try {
            await api.updateInvoice(invoiceId, { status });
            setData((prev) => ({
                ...prev,
                invoices: prev.invoices.map((i) => (i.id === invoiceId ? { ...i, status } : i)),
            }));
        } catch (err) {
            alert('Status-Update fehlgeschlagen: ' + err.message);
        }
    }

    async function handleDeleteInvoice(invoiceId) {
        if (!window.confirm('Diese Rechnung wirklich löschen?')) return;
        try {
            await api.deleteInvoice(invoiceId);
            await load();
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    if (loading) return <div className="empty">Lade…</div>;
    if (loadError) {
        return (
            <div>
                <Link to="/mandanten">← Zurück zur Liste</Link>
                <div className="panel" style={{ color: 'var(--color-danger)', marginTop: 12 }}>
                    Fehler: {loadError}
                </div>
            </div>
        );
    }

    return (
        <div className="page-centered">
            <div style={{ marginBottom: 8 }}>
                <Link to="/mandanten">← Zurück zur Liste</Link>
            </div>
            <div className="toolbar">
                <h1 style={{ margin: 0 }}>{data.name}</h1>
                <span className={'status status--' + (Number(data.active) === 1 ? 'aktiv' : 'inaktiv')}>
                    {Number(data.active) === 1 ? 'aktiv' : 'inaktiv'}
                </span>
                <div className="spacer" />
                <button className="btn" onClick={handleToggleActive}>
                    {Number(data.active) === 1 ? 'Auf inaktiv setzen' : 'Reaktivieren'}
                </button>
                <button className="btn btn--danger" onClick={handleDeleteMandant}>
                    Mandant löschen
                </button>
            </div>

            {Number(data.active) === 0 && (
                <div className="inactive-banner">
                    Dieser Mandant ist als <strong>inaktiv</strong> markiert.
                </div>
            )}

            {/* --- Stammdaten -------------------------------------------- */}
            <form className="panel" onSubmit={handleSave}>
                <h2>Stammdaten</h2>
                {saveError && <div className="login__error">{saveError}</div>}
                {savedNotice && (
                    <div className="panel" style={{ color: 'var(--color-success)', marginBottom: 12, padding: 8 }}>
                        Gespeichert.
                    </div>
                )}

                <div className="grid-2">
                    <div className="field">
                        <label>Kundennummer</label>
                        <input type="text" value={form.customer_no} onChange={(e) => updateField('customer_no', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>Mitgliedsnummer</label>
                        <input type="text" value={form.member_no} onChange={(e) => updateField('member_no', e.target.value)} />
                    </div>
                </div>

                <div className="field">
                    <label>Name *</label>
                    <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => updateField('name', e.target.value)}
                    />
                </div>

                <div className="grid-2">
                    <div className="field">
                        <label>Kontaktperson</label>
                        <input type="text" value={form.contact} onChange={(e) => updateField('contact', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>E-Mail</label>
                        <input type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
                    </div>
                </div>

                <h3 style={{ marginTop: 16 }}>Adresse</h3>
                <div className="field">
                    <label>Straße</label>
                    <input type="text" value={form.street} onChange={(e) => updateField('street', e.target.value)} />
                </div>
                <div className="grid-2">
                    <div className="field">
                        <label>PLZ</label>
                        <input type="text" value={form.zip} onChange={(e) => updateField('zip', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>Ort</label>
                        <input type="text" value={form.city} onChange={(e) => updateField('city', e.target.value)} />
                    </div>
                </div>

                <h3 style={{ marginTop: 16 }}>Vertrag & Lizenz</h3>
                <div className="grid-2">
                    <div className="field">
                        <label>Vertrag</label>
                        <input
                            type="text"
                            placeholder="z. B. 2025-01 bis 2026-12"
                            value={form.contract}
                            onChange={(e) => updateField('contract', e.target.value)}
                        />
                    </div>
                    <div className="field">
                        <label>Laufzeit ab</label>
                        <input
                            type="date"
                            value={form.contract_start}
                            onChange={(e) => updateField('contract_start', e.target.value)}
                        />
                    </div>
                </div>
                <div className="grid-2">
                    <div className="field">
                        <label>Lizenz</label>
                        <select value={form.license} onChange={(e) => updateField('license', e.target.value)}>
                            {LICENSE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div className="field">
                        <label>Kursset</label>
                        <select
                            value={form.course_set}
                            onChange={(e) => updateField('course_set', e.target.value)}
                        >
                            {courseSets.map((cs) => (
                                <option key={cs} value={cs}>{courseSetLabel(cs)}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <h3 style={{ marginTop: 16 }}>Kurszuteilung</h3>
                <div className="field">
                    <label>Art der Zuteilung (Notiz)</label>
                    <textarea
                        rows={4}
                        placeholder="z. B. feste Kurszuteilung, Self-Enrollment, Zuteilung über Gruppen …"
                        value={form.course_assignment}
                        onChange={(e) => updateField('course_assignment', e.target.value)}
                    />
                </div>

                <h3 style={{ marginTop: 16 }}>Notizen</h3>
                <MarkdownEditor
                    value={form.notes}
                    onChange={(md) => updateField('notes', md)}
                />

                <div className="muted" style={{ fontSize: 12, margin: '12px 0' }}>
                    Angelegt am {formatDate(data.created_at)}
                </div>

                <button type="submit" className="btn btn--primary" disabled={saving}>
                    {saving ? 'Speichere…' : 'Speichern'}
                </button>
            </form>

            {/* --- Rechnungen -------------------------------------------- */}
            <div className="panel">
                <div className="toolbar">
                    <h2 style={{ margin: 0 }}>Rechnungen ({data.invoices.length})</h2>
                    <div className="spacer" />
                    <button className="btn btn--primary" onClick={() => setCreatingInvoice(true)}>
                        Neue Rechnung
                    </button>
                </div>
                {data.invoices.length === 0 ? (
                    <div className="empty">Noch keine Rechnungen.</div>
                ) : (
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Nr.</th>
                                    <th>Datum</th>
                                    <th style={{ textAlign: 'right' }}>Betrag</th>
                                    <th>Status</th>
                                    <th>Beschreibung</th>
                                    <th style={{ width: 1 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.invoices.map((inv) => (
                                    <tr key={inv.id}>
                                        <td>{inv.invoice_no || <span className="muted">–</span>}</td>
                                        <td>{formatDateOnly(inv.date)}</td>
                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {formatEuro(inv.amount)}
                                        </td>
                                        <td>
                                            <select
                                                className="status-select"
                                                value={inv.status}
                                                onChange={(e) => handleInvoiceStatusChange(inv.id, e.target.value)}
                                            >
                                                {INVOICE_STATUSES.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td style={{ maxWidth: 340, whiteSpace: 'pre-wrap' }}>
                                            {inv.description || <span className="muted">–</span>}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <button className="btn btn--sm" onClick={() => setEditingInvoice(inv)}>Bearbeiten</button>{' '}
                                            <button className="btn btn--sm btn--danger" onClick={() => handleDeleteInvoice(inv.id)}>Löschen</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* --- Ereignisse -------------------------------------------- */}
            <div className="panel">
                <div className="toolbar">
                    <h2 style={{ margin: 0 }}>Ereignisse ({data.events.length})</h2>
                    <div className="spacer" />
                    <button className="btn btn--primary" onClick={() => setCreatingEvent(true)}>
                        Neues Ereignis
                    </button>
                </div>
                {data.events.length === 0 ? (
                    <div className="empty">Noch keine Ereignisse für diesen Mandanten.</div>
                ) : (
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Titel</th>
                                    <th>Status</th>
                                    <th>Autor</th>
                                    <th>Beschreibung</th>
                                    <th>Datum</th>
                                    <th style={{ width: 1 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.events.map((e) => (
                                    <tr key={e.id}>
                                        <td>{e.title}</td>
                                        <td>
                                            <select
                                                className="status-select"
                                                value={e.status}
                                                onChange={(ev) => handleEventStatusChange(e.id, ev.target.value)}
                                            >
                                                {EVENT_STATUSES.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                            <div style={{ marginTop: 4 }}><StatusBadge status={e.status} /></div>
                                        </td>
                                        <td>{e.author || <span className="muted">–</span>}</td>
                                        <td style={{ maxWidth: 380, whiteSpace: 'pre-wrap' }}>
                                            {e.description || <span className="muted">–</span>}
                                        </td>
                                        <td>{formatDate(e.created_at)}</td>
                                        <td>
                                            <button
                                                className="btn btn--sm btn--danger"
                                                onClick={() => handleDeleteEvent(e.id)}
                                            >
                                                Löschen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {creatingEvent && (
                <EventFormModal
                    mandanten={mandantenList}
                    defaultMandantId={id}
                    onClose={() => setCreatingEvent(false)}
                    onSaved={async () => { setCreatingEvent(false); await load(); }}
                />
            )}
            {creatingInvoice && (
                <InvoiceFormModal
                    mandantId={id}
                    onClose={() => setCreatingInvoice(false)}
                    onSaved={async () => { setCreatingInvoice(false); await load(); }}
                />
            )}
            {editingInvoice && (
                <InvoiceFormModal
                    mandantId={id}
                    initial={editingInvoice}
                    onClose={() => setEditingInvoice(null)}
                    onSaved={async () => { setEditingInvoice(null); await load(); }}
                />
            )}
        </div>
    );
}

function formatDate(s) {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('de-DE');
}

function formatDateOnly(s) {
    if (!s) return '';
    // Erwartet ISO-Date 'YYYY-MM-DD'.
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString('de-DE');
}

function formatEuro(n) {
    if (n === null || n === undefined) return '';
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(Number(n));
}
